import {
  ButtonGroup,
  Center,
  Flex,
  type FlexProps,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Tooltip,
  useBoolean,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { CapacityAllocationTypeEnum } from "@src/__generated__/graphql";
import {
  CapacityAllocationRepetitionTypeEnum,
  ReopenCapacityAllocationItemDocument,
  type ReopenCapacityAllocationItemMutation,
  type ReopenCapacityAllocationItemMutationVariables,
} from "@src/__generated__/urql-graphql";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import { Icon, TrackedTimeIcon } from "@src/components/ui-kit/Icon";
import { TimeTrackingButton } from "@src/components/widgets/Modals/ModalCommunication/components";
import { client } from "@src/services/client";
import type { CapacityAllocationCompleteOptions } from "@src/stores/CompleteCapacityAllocationModalStore";
import type { AffectAllocationOccurrences } from "@src/types/planning";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { isTrackingEnabled } from "@src/utils/time-tracking";
import type { AllocationItemProps } from "@src/widgets/AllocationItem/AllocationItem";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { type FC, Fragment, useState } from "react";

const ACTIONS_OVERLAY_PADDING = 2;
const ACTION_BUTTON_HEIGHT = `${32 - ACTIONS_OVERLAY_PADDING * 2}px`;

type AllocationItemActionsOverlayProps = Pick<AllocationItemProps, "userItem"> &
  FlexProps;

function getCompleteModalItem(
  userItem: AllocationItemProps["userItem"],
): CapacityAllocationCompleteOptions["item"] | undefined {
  if (!userItem.stats?.task_id) return undefined;
  return {
    id: userItem.id,
    total_time: userItem.total_time,
    time_tracking_work_type_id: userItem.time_tracking_work_type_id,
    date: new Date(userItem.date),
    task_id: userItem.stats.task_id,
    task_name: userItem.stats.task_name,
    note: userItem.note,
    item_id: userItem.id,
    user_id: userItem.user.id,
    budget_item_title: userItem.stats.our_work_budget_item_title,
    project_code: userItem.stats.project_code,
  };
}

export const AllocationItemActionsOverlay: FC<AllocationItemActionsOverlayProps> =
  observer(function AllocationItemActionsOverlay({
    userItem: allocationItem,
    ...props
  }) {
    const {
      completed,
      user,
      id,
      has_running_timer,
      time_tracking_work_type_id,
      date,
      stats,
      createdBy,
      isRepeating,
    } = allocationItem;
    const resourcePlanningStore = useResourcePlanningDailyViewStore();
    const [fetchingItemDetail, setFetchingItemDetail] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);

    const hasMultipleAttendees = (stats?.items_count ?? 0) > 1;

    const {
      taskDetailModalStore,
      authStore,
      completeCapacityAllocationModalStore,
    } = useStore();

    const buttonStyle = {
      h: ACTION_BUTTON_HEIGHT,
      w: ACTION_BUTTON_HEIGHT,
      color: "blue.600",
      bg: "grey.50",
      p: 0,
      rounded: "base",
      _hover: {
        bg: "grey.100",
      },
      ...(completed && {
        bg: "grey.100",
        _hover: {
          bg: "grey.200",
        },
      }),
    };

    const trackingButtonLabel = computed(() => {
      return has_running_timer ? t`Stop tracking` : t`Start tracking`;
    }).get();

    const onOpenTask = () => {
      if (!stats?.task_id) return;
      taskDetailModalStore.disclosureState.onOpen({
        id: stats.task_id,
        allocationType: allocationItem.type,
      });
    };

    const onComplete = () => {
      if (!allocationItem.stats?.task_id) return;
      completeCapacityAllocationModalStore.modalState.onOpen({
        item: getCompleteModalItem(allocationItem),
        resourcePlanningStore,
        mode: "complete",
      });
    };

    const onTimeTrack = () => {
      completeCapacityAllocationModalStore.modalState.onOpen({
        item: getCompleteModalItem(allocationItem),
        resourcePlanningStore,
        mode: "timeTracking",
      });
    };

    const onTimeTrackingChange = (running: boolean) => {
      allocationItem.setHasRunningTimer(running);
      resourcePlanningStore.syncData();
    };

    const onEdit = (affectOccurrences: AffectAllocationOccurrences) => {
      resourcePlanningStore.handleEdit(
        allocationItem,
        affectOccurrences,
        (isLoading) => {
          setFetchingItemDetail(isLoading);
        },
      );
    };

    const [isReopenButtonLoading, setIsReopenButtonLoading] = useBoolean(false);
    const onReopen = async () => {
      setIsReopenButtonLoading.on();
      const res = await client.mutation<
        ReopenCapacityAllocationItemMutation,
        ReopenCapacityAllocationItemMutationVariables
      >(ReopenCapacityAllocationItemDocument, {
        input: { id, completed: false },
      });

      if (res.data?.reopenCapacityAllocationItem) {
        resourcePlanningStore.appStore.UIStore.toast({
          title: t`Capacity allocation has been reopened`,
          status: "success",
        });
        completeCapacityAllocationModalStore.locallyToggleCompleted(
          res.data.reopenCapacityAllocationItem,
          undefined,
          resourcePlanningStore,
        );
      }
      setIsReopenButtonLoading.off();
    };

    const isImportedLoading =
      resourcePlanningStore.deletingAllocationItemId.has(
        stats?.capacity_allocation_id ?? "",
      );

    const handleAcceptImportedAllocation = () => {
      resourcePlanningStore.handleEdit(allocationItem, "allOccurrences");
    };

    const handleAcceptImportedAllocationForUser = () => {
      resourcePlanningStore.acceptImportedAllocationForUser(allocationItem);
    };

    const handleDeclineImportedAllocation = () => {
      if (!stats) return;
      resourcePlanningStore.declineImportedAllocation(
        stats.capacity_allocation_id,
      );
    };

    const handleDeclineImportedAllocationForUser = () => {
      if (!stats) return;
      resourcePlanningStore.declineImportedAllocationForUser(
        stats.capacity_allocation_id,
        allocationItem.user.id,
      );
    };

    const canUpdate =
      can("planning_update_all") ||
      can({
        do: "planning_update_own",
        eval: ({ id }) =>
          id === createdBy?.id ||
          id === stats?.capacity_allocation_created_by_id,
      });

    return (
      <Flex
        pos="absolute"
        zIndex={10}
        bottom="0"
        left="0"
        w="full"
        p={`${ACTIONS_OVERLAY_PADDING}px`}
        {...props}
        display={showOverlay || fetchingItemDetail ? "flex" : props.display}
      >
        {allocationItem.type === CapacityAllocationTypeEnum.Imported ? (
          canUpdate && (
            <>
              <ButtonGroup
                onClick={(e) => e.stopPropagation()}
                size="xs"
                spacing="0.5"
                variant="subtle"
              >
                {hasMultipleAttendees ? (
                  <Menu
                    onClose={() => {
                      setShowOverlay(false);
                    }}
                    onOpen={() => {
                      setShowOverlay(true);
                    }}
                  >
                    <Tooltip
                      label={t`Accept imported allocation`}
                      placement="top"
                    >
                      <MenuButton
                        as={IconButton}
                        aria-label={t`Accept imported allocation`}
                        colorScheme="green"
                        icon={<Icon name="check" boxSize="3" />}
                        isLoading={isImportedLoading}
                      />
                    </Tooltip>
                    <Portal>
                      <MenuList>
                        <MenuItem onClick={handleAcceptImportedAllocation}>
                          <Trans>Accept for all</Trans>
                        </MenuItem>
                        <MenuItem
                          onClick={handleAcceptImportedAllocationForUser}
                        >
                          <Trans>Accept for this user</Trans>
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                ) : (
                  <Tooltip
                    label={t`Accept imported allocation`}
                    placement="top"
                  >
                    <IconButton
                      aria-label={t`Accept imported allocation`}
                      colorScheme="green"
                      icon={<Icon name="check" boxSize="3" />}
                      isLoading={isImportedLoading}
                      onClick={handleAcceptImportedAllocation}
                    />
                  </Tooltip>
                )}

                {hasMultipleAttendees ? (
                  <Menu
                    onClose={() => {
                      setShowOverlay(false);
                    }}
                    onOpen={() => {
                      setShowOverlay(true);
                    }}
                  >
                    <Tooltip
                      label={t`Decline imported allocation`}
                      placement="top"
                    >
                      <MenuButton
                        as={IconButton}
                        aria-label={t`Decline imported allocation`}
                        colorScheme="red"
                        icon={<Icon name="x-close" boxSize="3" />}
                        isLoading={isImportedLoading}
                      />
                    </Tooltip>
                    <Portal>
                      <MenuList>
                        <MenuItem onClick={handleDeclineImportedAllocation}>
                          <Trans>Decline for all</Trans>
                        </MenuItem>
                        <MenuItem
                          onClick={handleDeclineImportedAllocationForUser}
                        >
                          <Trans>Decline for this user</Trans>
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                ) : (
                  <Tooltip
                    label={t`Decline imported allocation`}
                    placement="top"
                  >
                    <IconButton
                      aria-label={t`Decline imported allocation`}
                      colorScheme="red"
                      icon={<Icon name="x-close" boxSize="3" />}
                      isLoading={isImportedLoading}
                      onClick={handleDeclineImportedAllocation}
                    />
                  </Tooltip>
                )}
              </ButtonGroup>
            </>
          )
        ) : (
          <ButtonGroup
            bg="grey.50"
            colorScheme="grey"
            isAttached
            onClick={(e) => e.stopPropagation()}
            rounded="md"
            size="sm"
            variant="unstyled"
          >
            {canUpdate && (
              <Tooltip label={t`Edit`}>
                {isRepeating ? (
                  <Menu
                    onClose={() => {
                      setShowOverlay(false);
                    }}
                    onOpen={() => {
                      setShowOverlay(true);
                    }}
                  >
                    <MenuButton
                      as={IconButton}
                      aria-label={t`Edit allocation`}
                      icon={<EditIcon isLoading={fetchingItemDetail} />}
                    />
                    <Portal>
                      <MenuList>
                        <MenuItem onClick={() => onEdit("currentItem")}>
                          <Trans>This allocation</Trans>
                        </MenuItem>
                        {stats?.repetition !==
                          CapacityAllocationRepetitionTypeEnum.Once && (
                          <MenuItem onClick={() => onEdit("thisDay")}>
                            <Trans>This day (all users)</Trans>
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => onEdit("allOccurrences")}>
                          <Trans>All occurrences</Trans>
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                ) : (
                  <IconButton
                    aria-label={t`Edit allocation`}
                    icon={<EditIcon isLoading={fetchingItemDetail} />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEdit("allOccurrences");
                    }}
                  />
                )}
              </Tooltip>
            )}

            {completed ? (
              <Tooltip label={t`Reopen`}>
                <IconButton
                  sx={buttonStyle}
                  aria-label={t`Reopen`}
                  icon={<Icon name="flip-backward" />}
                  isLoading={isReopenButtonLoading}
                  onClick={onReopen}
                />
              </Tooltip>
            ) : (
              <Fragment>
                <Tooltip label={t`Complete`}>
                  <IconButton
                    sx={buttonStyle}
                    aria-label={t`Complete`}
                    icon={<Icon name="check" pointerEvents="none" />}
                    isDisabled={
                      stats && !isTrackingEnabled(stats, new Date(date))
                    }
                    onClick={onComplete}
                  />
                </Tooltip>
                {(allocationItem.type !== CapacityAllocationTypeEnum.Task ||
                  (allocationItem.type === CapacityAllocationTypeEnum.Task &&
                    can("timeTracking_timer_use_manual"))) && (
                  <Tooltip label={t`Track time`}>
                    <IconButton
                      sx={buttonStyle}
                      aria-label={t`Track time`}
                      icon={<TrackedTimeIcon pointerEvents="none" />}
                      isDisabled={
                        stats && !isTrackingEnabled(stats, new Date(date))
                      }
                      onClick={onTimeTrack}
                    />
                  </Tooltip>
                )}
                {!taskDetailModalStore.hideTimeTrackingButton(
                  allocationItem.type,
                ) &&
                  user.id === authStore.user?.id && (
                    <TimeTrackingButton
                      label={trackingButtonLabel}
                      sx={buttonStyle}
                      onTimeTrackingChange={onTimeTrackingChange}
                      variant="iconButton"
                      icon={
                        <Icon
                          name={has_running_timer ? "pause-circle" : "play"}
                          variant="solid"
                        />
                      }
                      taskId={stats?.task_id ?? ""}
                      timeTrackingSettings={{
                        tracking_enabled: stats?.tracking_enabled ?? false,
                        tracking_enabled_from: stats?.tracking_enabled_from
                          ? new Date(stats.tracking_enabled_from)
                          : undefined,
                        tracking_enabled_to: stats?.tracking_enabled_to
                          ? new Date(stats.tracking_enabled_to)
                          : undefined,
                      }}
                      capacityAllocationItemId={allocationItem.id}
                      trackForUser={user.id}
                      trackForDate={new Date(date)}
                      trackForPosition={time_tracking_work_type_id ?? undefined}
                      note={allocationItem.note}
                    />
                  )}
              </Fragment>
            )}
            <Tooltip label={t`Open task`}>
              <IconButton
                sx={buttonStyle}
                aria-label={t`Open task`}
                icon={<Icon name="check-done-01" pointerEvents="none" />}
                onClick={onOpenTask}
              />
            </Tooltip>
          </ButtonGroup>
        )}
      </Flex>
    );
  });

const EditIcon: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  return isLoading ? (
    <Center>
      <Spinner size="xs" />
    </Center>
  ) : (
    <Icon name="edit-02" w="3.5" h="3.5" />
  );
};
